import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { StaffResponse } from '../api/generated';
import type { CompetenceNameById } from '../components/PatientDetails/preferredStaffHelpers';
import { fetchStaff, fetchStaffCompetences } from '../services/staffService';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  clearStaffError,
  selectStaff,
  selectStaffError,
  selectStaffLoadedLocationId,
  setError,
  setStaff
} from '../store/staff/staffSlice';
import {
  selectStaffCompetences,
  setCompetences
} from '../store/staffCompetences/staffCompetencesSlice';
import { showError } from '../store/errorDialog/errorDialogSlice';

export interface StaffOptions {
  staff: StaffResponse[];
  competenceNameById: CompetenceNameById;
  // Whether this location's roster has arrived. Without it an empty `staff` reads as "nobody
  // works here", so the field would state that a patient has no preferred staff when the truth
  // is that we cannot know yet.
  isLoaded: boolean;
  hasError: boolean;
  // The loader's effect cannot re-run on its own after a failure: the drawer stays mounted and
  // none of its other dependencies change. Clearing the error is what re-arms it.
  retry: () => void;
}

const STAFF_LOAD_ERROR = 'Failed to load staff';

/**
 * Loads the staff and staff competences the Preferred staff field needs.
 *
 * Call this once from a drawer that renders PatientDetailsCard, the same way `useRoomProperties`
 * is called, and read the result with `useStaffOptions`. Deliberately not `useStaff`: that is the
 * settings-page CRUD hook, which refetches and replaces the whole roster on every location change.
 */
export function usePreferredStaffLoader(locationId: string | null, isOpen: boolean): void {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const loadedLocationId = useAppSelector(selectStaffLoadedLocationId);
  const error = useAppSelector(selectStaffError);
  const competences = useAppSelector(selectStaffCompetences);
  const hasCompetences = competences.length > 0;

  // Guarded on store state rather than a ref, so React's double-invoked effects in development
  // cannot leave the roster permanently unfetched: an eagerly-set ref would make the second run
  // skip while the first run's response was already discarded by its own cleanup.
  useEffect(() => {
    if (!isOpen || !locationId || loadedLocationId === locationId || error !== null) return;
    let cancelled = false;
    void fetchStaff(locationId)
      .then((data) => {
        if (!cancelled) dispatch(setStaff({ staff: data, locationId }));
      })
      .catch((fetchError: unknown) => {
        console.error('Failed to load staff for location', locationId, fetchError);
        if (cancelled) return;
        dispatch(setError(STAFF_LOAD_ERROR));
        dispatch(showError({ message: t('fetch-error') }));
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, locationId, loadedLocationId, error, dispatch, t]);

  // Competences are account-wide rather than per location, and only decorate the names, so a
  // failure here degrades the labels rather than the field itself. It gets no separate retry.
  useEffect(() => {
    if (!isOpen || hasCompetences) return;
    let cancelled = false;
    void fetchStaffCompetences()
      .then((data) => {
        if (!cancelled) dispatch(setCompetences(data));
      })
      .catch((fetchError: unknown) => {
        console.error('Failed to load staff competences', fetchError);
        if (!cancelled) dispatch(showError({ message: t('fetch-error') }));
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, hasCompetences, dispatch, t]);
}

/** Reads what `usePreferredStaffLoader` put in the store, for the given location. */
export function useStaffOptions(locationId: string | null): StaffOptions {
  const dispatch = useAppDispatch();
  const staff = useAppSelector(selectStaff);
  const competences = useAppSelector(selectStaffCompetences);
  const loadedLocationId = useAppSelector(selectStaffLoadedLocationId);
  const error = useAppSelector(selectStaffError);

  const competenceNameById = useMemo(
    () => new Map(competences.map((competence) => [competence.id, competence.name])),
    [competences]
  );

  const retry = useCallback(() => {
    dispatch(clearStaffError());
  }, [dispatch]);

  return {
    staff,
    competenceNameById,
    isLoaded: locationId !== null && loadedLocationId === locationId,
    hasError: error !== null,
    retry
  };
}
